/**
 * ✅ Migrated to authStore_v1 + Axios - 2026-01-07
 * 
 * Fetch User Data Hook
 * 
 * Fetches user data with organizations from GraphQL API via Axios.
 * Returns RAW data - App.tsx handles store hydration.
 * 
 * Uses: React Query + Axios (removed Apollo Client for simplicity)
 */

import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/dataProvider/axios";

// GraphQL query string (same as before, just using axios now)
const GET_USER_DATA_QUERY = `
  query GetUserData {
    user {
      id
      email
      organizations {
        id
        orgName
        orgType
        instanceUrl
        salesforceOrgId
        role
        myRole {
          id
          name
          managementLevel
          capabilityKeys
        }
        latestMetadataRetrieval {
          id
          status
          progressPercentage
          retrievalType
        }
        latestRefreshOperation {
          id
          metadataRetrievalId
          status
        }
        metadataTypeCounts {
          count
          metadataType
        }
      }
    }
  }
`;

/**
 * API Response Types
 */
interface MetadataTypeCount {
  count: number;
  metadataType: string;
}

interface LatestMetadataRetrieval {
  id: string;
  status: string;
  progressPercentage: number;
  retrievalType: 'METADATA_TYPE' | 'FULL_ORG_REFRESH' | 'INITIAL_RETRIEVAL';
}

interface LatestRefreshOperation {
  id: string;
  metadataRetrievalId: string;
  status: 'QUEUED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
}

interface MyRole {
  id: string;
  name: string;
  managementLevel: 'OWNER' | 'ADMIN' | 'MEMBER';
  capabilityKeys: string[];
}

interface Organization {
  id: string;
  orgName: string;
  orgType: string;
  instanceUrl: string;
  salesforceOrgId: string;
  // Current user's management level in this org; null if not assigned a role.
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null;
  // Current user's full assigned role (name + granted services); null if unassigned.
  myRole: MyRole | null;
  latestMetadataRetrieval: LatestMetadataRetrieval | null;
  latestRefreshOperation: LatestRefreshOperation | null;
  metadataTypeCounts: MetadataTypeCount[];
}

interface User {
  id: string;
  email: string;
  organizations: Organization[];
}

interface GetUserDataResponse {
  user: User;
}

/**
 * Hook to fetch user data
 * 
 * Returns RAW data from API - no mapping, no store setting.
 * App.tsx handles store hydration via setUser() which auto-sets orgs.
 * 
 * @param token - JWT token for authentication
 * @returns React Query result with user data
 */
export function useFetchUserData(token: string | null) {
  return useQuery<GetUserDataResponse, Error>({
    queryKey: ["user-data", token],
    queryFn: async () => {
      if (!token) {
        throw new Error("No token provided");
      }


      // GraphQL request via Axios (simple POST to /graphql)
      const response = await axiosInstance.post<GetUserDataResponse>('/graphql', {
        query: GET_USER_DATA_QUERY,
      });

      return response.data; // Axios interceptor already unwrapped { data: {...} }
    },
    enabled: !!token, // Only run if token exists
    retry: false,
  });
}

